import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Product } from '../types';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useNotification } from '../hooks/useNotification';
import { useCart } from '../hooks/useCart';
import { useQuery } from '@tanstack/react-query';
import { productService } from '../services/product.service';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  ChevronLeft,
  ShoppingBag,
  Star,
  Minus,
  Plus,
  ZoomIn,
  X
} from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';

export const ProductDetailPage: React.FC = () => {
  const { productCode } = useParams<{ productCode: string }>();
  const { showSuccess } = useNotification();
  const { addItem } = useCart();
  const [activeImage, setActiveImage] = useState(0);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  const { data: product, isLoading: loading, error, isError } = useQuery<Product>({
    queryKey: ['product', productCode],
    queryFn: () => productService.getProduct(productCode || ''),
    enabled: !!productCode,
  });

  if (loading) return <div className="container py-20 flex justify-center"><LoadingSpinner /></div>;

  if (isError || !product) {
    console.error('Product fetch error:', error);
    return (
      <div className="container py-20 text-center">
        <h2 className="text-2xl font-bold">{isError ? 'Error Loading Product' : 'Product Not Found'}</h2>
        <p className="text-muted-foreground mt-2">
          {isError
            ? `Failed to load product details. ${(error as Error)?.message || 'Please try again.'}`
            : 'The product you are looking for could not be found.'
          }
        </p>
        <Button asChild className="mt-4">
          <Link to="/products">Back to Shop</Link>
        </Button>
      </div>
    );
  }

  const productId = product.id || product.productId || '';
  const isInStock = product.isInStock !== undefined ? product.isInStock : (product.status === 'published' || product.stock > 0);
  const categoryDisplay = product.categoryName || product.category;

  const handleAddToCart = () => {
    addItem({
      id: productId,
      productId: productId,
      name: product.name,
      price: product.price,
      image: product.thumbnailUrl || (product.images && product.images[0]?.url) || '',
      productCode: product.productCode,
    }, quantity);
    showSuccess(`${product.name} added to cart!`);
  };

  const incrementQuantity = () => {
    if (product.stock !== undefined) {
      if (quantity < product.stock) setQuantity(prev => prev + 1);
    } else {
      setQuantity(prev => prev + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) setQuantity(prev => prev - 1);
  };

  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">
      <header className="mb-8">
        <Button variant="ghost" size="sm" className="pl-0 hover:bg-transparent group" asChild>
          <Link to="/products" className="flex items-center text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeft className="mr-1 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-xs font-bold uppercase tracking-wider">Back to Products</span>
          </Link>
        </Button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
        {/* Product Gallery */}
        <div className="lg:col-span-7 flex flex-col md:flex-row-reverse md:items-start gap-4 md:gap-6">
          <Dialog.Root open={isZoomOpen} onOpenChange={setIsZoomOpen}>
            <Dialog.Trigger asChild>
              <div className="w-full aspect-square relative overflow-hidden rounded-[2.5rem] bg-gray-50 dark:bg-gray-900 border border-border/40 shadow-sm group cursor-zoom-in">
                {product.images && product.images[activeImage] ? (
                  <img
                    src={product.images[activeImage].url}
                    alt={product.images[activeImage].alt || product.name}
                    className="h-full w-full object-cover object-center transition-all duration-700 ease-in-out group-hover:scale-110"
                  />
                ) : product.thumbnailUrl ? (
                  <img
                    src={product.thumbnailUrl}
                    alt={product.name}
                    className="h-full w-full object-cover object-center transition-all duration-700 ease-in-out group-hover:scale-110"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted-foreground/30 font-bold uppercase tracking-[0.2em] text-xs">
                    No Image Available
                  </div>
                )}
                {/* REMOVED: stock left badge */}
                {!isInStock && (
                  <Badge variant="secondary" className="absolute top-6 left-6 shadow-lg bg-black text-white">Out of Stock</Badge>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                  <ZoomIn className="text-white drop-shadow-md w-12 h-12 opacity-80" />
                </div>
              </div>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 bg-black/90 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
              <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full h-full max-w-none translate-x-[-50%] translate-y-[-50%] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-200 outline-none">
                <div className="relative w-full h-full flex items-center justify-center p-4" onClick={() => setIsZoomOpen(false)}>
                  <Dialog.Close className="absolute right-4 top-4 z-50 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors">
                    <X className="h-6 w-6" />
                    <span className="sr-only">Close</span>
                  </Dialog.Close>
                  <img
                    src={product.images && product.images[activeImage] ? product.images[activeImage].url : product.thumbnailUrl || ''}
                    alt={product.name}
                    className="max-h-full max-w-full object-contain cursor-zoom-out"
                  />
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          {product.images && product.images.length > 1 && (
            <div className="flex md:flex-col gap-3 md:gap-4 overflow-x-auto md:overflow-y-auto no-scrollbar h-16 md:h-auto md:w-16 md:max-h-[500px]">
              {product.images.map((img, idx) => (
                <button
                  key={img.id || idx}
                  onClick={() => setActiveImage(idx)}
                  className={`relative h-14 w-14 md:h-14 md:w-14 flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all duration-300 ${activeImage === idx
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-transparent hover:border-primary/40 opacity-70 hover:opacity-100'
                    }`}
                >
                  <img src={img.url} alt={img.alt || product.name} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="lg:col-span-5 flex flex-col pt-2">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[10px] font-black uppercase text-primary/60 tracking-[0.2em]">
                {categoryDisplay}
              </span>
              {product.rating && (
                <div className="flex items-center gap-1.5 px-3 py-1 bg-yellow-400/10 rounded-full">
                  <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                  <span className="text-xs font-black text-yellow-600">{product.rating}</span>
                </div>
              )}
            </div>

            <h1 className="text-3xl md:text-5xl font-black tracking-tight text-[#111418] dark:text-white mb-4">
              {product.name}
            </h1>

            <div className="flex items-baseline gap-3">
              <span className="text-3xl font-black text-primary">${product.price}</span>
              {product.compareAtPrice && (
                <span className="text-lg text-muted-foreground line-through font-medium">
                  ${product.compareAtPrice}
                </span>
              )}
            </div>
          </div>

          <div className="prose prose-sm text-gray-500 dark:text-gray-400 mb-10 leading-relaxed font-medium">
            <p>{product.description}</p>
          </div>

          <Separator className="mb-10 opacity-50" />

          {product.variants && product.variants.length > 0 && product.options && (
            <div className="mb-10 space-y-8">
              {product.options.map(option => (
                <div key={option.name} className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-[11px] font-black uppercase tracking-[0.15em] text-foreground/70">
                      Select {option.name}
                    </label>
                    <button className="text-[10px] font-bold text-primary hover:underline">Size Guide</button>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {option.values.map((value: string) => (
                      <button
                        key={value}
                        onClick={() => setSelectedVariant(value)}
                        className={`min-w-[60px] h-11 px-5 rounded-xl text-xs font-bold border transition-all duration-200 ${selectedVariant === value
                          ? "bg-primary border-primary text-white shadow-lg shadow-primary/25"
                          : "bg-white dark:bg-gray-900 border-border hover:border-primary/50 text-foreground"
                          }`}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-8 mb-12">
            <div className="space-y-3">
              <label className="text-[11px] font-black uppercase tracking-[0.15em] text-foreground/70">Quantity</label>
              <div className="flex items-center gap-6">
                <div className="flex items-center bg-gray-50 dark:bg-gray-900 rounded-2xl p-1.5 border border-border/50">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={decrementQuantity}
                    disabled={quantity <= 1}
                    className="size-10 rounded-xl hover:bg-white dark:hover:bg-gray-800 transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-10 text-center font-black text-sm">{quantity}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={incrementQuantity}
                    disabled={product.stock !== undefined && quantity >= product.stock}
                    className="size-10 rounded-xl hover:bg-white dark:hover:bg-gray-800 transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {/* REMOVED: stock items left indicator */}
              </div>
            </div>

            <Button
              onClick={handleAddToCart}
              disabled={!isInStock}
              size="lg"
              className="w-full h-16 rounded-2xl text-base font-black shadow-2xl shadow-primary/20 transition-all hover:-translate-y-1 active:scale-95 bg-primary hover:bg-primary/90"
            >
              <ShoppingBag className="mr-3 h-5 w-5" />
              {isInStock ? 'Add to Shopping Bag' : 'Join a Waitlist'}
            </Button>
          </div>

          {/* REMOVED: Trust features */}
        </div>
      </div>
    </div>
  );
};
